import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Package, AlertTriangle, XCircle, CheckCircle, Search, Printer, X } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

const STATUS_CFG: Record<string, { label: string; cls: string; icon: any }> = {
  zero:    { label: 'Zero',    cls: 'bg-red-50 text-red-700 border-red-200',        icon: XCircle },
  baixo:   { label: 'Baixo',   cls: 'bg-amber-50 text-amber-700 border-amber-200',  icon: AlertTriangle },
  ok:      { label: 'OK',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  excesso: { label: 'Excesso', cls: 'bg-blue-50 text-blue-700 border-blue-200',     icon: Package },
}

// FIX (09/09, pedido do cliente): default 'true' em ativo reproduz o comportamento
// antigo da tela (só mostrava produto ativo) sem precisar de default no backend.
const FILTROS_INICIAIS = {
  categoria: '', status: '', filial: '', deposito: '', fornecedor: '', marca: '',
  ativo: 'true' as 'true' | 'false' | '',
  qtdMin: '', qtdMax: '', valorMin: '', valorMax: '',
}

export default function RelatorioEstoquePage() {
  const [busca, setBusca] = useState('')
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS)
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (filtros.categoria) p.categoria_id = filtros.categoria
    if (filtros.status) p.status = filtros.status
    if (filtros.filial) p.filial_id = filtros.filial
    if (filtros.deposito) p.deposito_id = filtros.deposito
    if (filtros.fornecedor) p.fornecedor_id = filtros.fornecedor
    if (filtros.marca) p.marca = filtros.marca
    if (filtros.ativo) p.ativo = filtros.ativo
    if (filtros.qtdMin) p.qtd_min = filtros.qtdMin
    if (filtros.qtdMax) p.qtd_max = filtros.qtdMax
    if (filtros.valorMin) p.valor_min = filtros.valorMin
    if (filtros.valorMax) p.valor_max = filtros.valorMax
    return p
  }, [filtros])

  const { data, isLoading } = useQuery<any>({
    queryKey: ['relatorio-estoque', params],
    queryFn: async () => (await api.get('/relatorios/estoque', { params })).data,
  })

  const { data: categorias = [] } = useQuery<any[]>({
    queryKey: ['categorias'],
    queryFn: async () => (await api.get('/cadastros/categorias')).data,
  })
  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-estoque'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const { data: depositos = [] } = useQuery<any[]>({
    queryKey: ['depositos-estoque'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })
  const { data: fornecedores = [] } = useQuery<any[]>({
    queryKey: ['fornecedores-estoque'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor' } })).data,
  })

  const depositosDaFilial = useMemo(
    () => depositos.filter((d: any) => !filtros.filial || String(d.filial_id) === filtros.filial),
    [depositos, filtros.filial],
  )

  const setFiltro = (campo: keyof typeof FILTROS_INICIAIS, valor: string) => {
    setFiltros(f => {
      const novo = { ...f, [campo]: valor }
      if (campo === 'filial') novo.deposito = ''  // depósito antigo pode não ser dessa filial
      return novo
    })
  }

  const limparFiltros = () => { setFiltros(FILTROS_INICIAIS); setBusca('') }
  const filtrosAtivos =
    Object.entries(filtros).filter(([k, v]) => v && !(k === 'ativo' && v === 'true')).length +
    (busca ? 1 : 0)

  const produtosFiltrados = useMemo(() => {
    if (!data?.produtos) return []
    let lista = data.produtos
    const q = busca.trim().toLowerCase()
    if (q) {
      lista = lista.filter((p: any) =>
        p.descricao.toLowerCase().includes(q) ||
        p.codigo.toLowerCase().includes(q),
      )
    }
    if (!sort) return lista
    const val = (p: any) => {
      switch (sort.key) {
        case 'codigo': return p.codigo
        case 'descricao': return p.descricao.toLowerCase()
        case 'categoria': return p.categoria.toLowerCase()
        case 'atual': return p.estoque_atual
        case 'minimo': return p.estoque_minimo
        case 'custo': return p.valor_estoque_custo
        case 'venda': return p.valor_estoque_venda
        default: return ''
      }
    }
    const arr = [...lista]
    arr.sort((a, b) => {
      const va = val(a), vb = val(b)
      if (va < vb) return sort.dir === 'asc' ? -1 : 1
      if (va > vb) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [data, busca, sort])

  const toggleSort = (key: string) =>
    setSort(s => (s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }))
  const arrow = (key: string) => (sort?.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  if (isLoading) return <div className="text-ink-500">Carregando...</div>
  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-ink-900">Posição de Estoque</h2>
          <p className="text-sm text-ink-500 mt-0.5">
            Monitore ruptura, excesso e valor total do estoque.
            {data.filtrado_por_local && (
              <span className="ml-1 font-medium text-brand-700">Mostrando saldo da loja/depósito selecionado.</span>
            )}
          </p>
        </div>
        <button onClick={() => window.print()} className="btn-secondary">
          <Printer size={14} /> Imprimir
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Produtos" value={String(data.total_produtos)} icon={Package} tone="brand" />
        <KPI label="Em Ruptura" value={String(data.contagem.zero + data.contagem.baixo)} icon={AlertTriangle} tone="red" />
        <KPI label="Estoque Zero" value={String(data.contagem.zero)} icon={XCircle} tone="red" />
        <KPI label="Valor Total (Custo)" value={formatMoney(data.valor_total_custo)} icon={Package} tone="amber" />
      </div>

      {/* Alertas */}
      {data.alertas.length > 0 && (
        <div className="card card-body bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-red-900 mb-2">
                {data.alertas.length} {data.alertas.length === 1 ? 'produto' : 'produtos'} em alerta de ruptura
              </div>
              <div className="space-y-1 text-sm">
                {data.alertas.slice(0, 10).map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between text-red-900">
                    <span>
                      <span className="font-mono text-xs text-red-700 mr-2">{p.codigo}</span>
                      <span className="font-medium">{p.descricao}</span>
                      <span className="text-red-700 text-xs ml-2">— {p.categoria}</span>
                    </span>
                    <span className="font-medium">
                      {p.status === 'zero'
                        ? <span className="text-red-700">ESTOQUE ZERO</span>
                        : <span>{Number(p.estoque_atual).toLocaleString('pt-BR')} / mín {Number(p.estoque_minimo).toLocaleString('pt-BR')} {p.unidade}</span>}
                    </span>
                  </div>
                ))}
                {data.alertas.length > 10 && (
                  <div className="text-xs text-red-700 italic pt-1">
                    + {data.alertas.length - 10} outros produtos em ruptura.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card card-body space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="label">Buscar</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                className="input pl-9"
                placeholder="Código ou descrição..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="label">Categoria</label>
            <select className="input min-w-[160px]" value={filtros.categoria} onChange={(e) => setFiltro('categoria', e.target.value)}>
              <option value="">Todas</option>
              {categorias.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input min-w-[130px]" value={filtros.status} onChange={(e) => setFiltro('status', e.target.value)}>
              <option value="">Todos</option>
              <option value="zero">Zero</option>
              <option value="baixo">Baixo</option>
              <option value="ok">OK</option>
              <option value="excesso">Excesso</option>
            </select>
          </div>
          <div>
            <label className="label">Ativo</label>
            <select className="input min-w-[110px]" value={filtros.ativo} onChange={(e) => setFiltro('ativo', e.target.value)}>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
              <option value="">Todos</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">Filial</label>
            <select className="input min-w-[170px]" value={filtros.filial} onChange={(e) => setFiltro('filial', e.target.value)}>
              <option value="">Todas (consolidado)</option>
              {filiais.map((f: any) => (
                <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social || `Filial ${f.codigo}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Depósito</label>
            <select className="input min-w-[160px]" value={filtros.deposito} onChange={(e) => setFiltro('deposito', e.target.value)}>
              <option value="">Todos</option>
              {depositosDaFilial.map((d: any) => (
                <option key={d.id} value={d.id}>{d.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Fornecedor</label>
            <select className="input min-w-[180px]" value={filtros.fornecedor} onChange={(e) => setFiltro('fornecedor', e.target.value)}>
              <option value="">Todos</option>
              {fornecedores.map((f: any) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Marca</label>
            <select className="input min-w-[140px]" value={filtros.marca} onChange={(e) => setFiltro('marca', e.target.value)}>
              <option value="">Todas</option>
              {(data.marcas_disponiveis || []).map((m: string) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">Qtd. mínima</label>
            <input type="number" className="input w-28" value={filtros.qtdMin}
              onChange={(e) => setFiltro('qtdMin', e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="label">Qtd. máxima</label>
            <input type="number" className="input w-28" value={filtros.qtdMax}
              onChange={(e) => setFiltro('qtdMax', e.target.value)} placeholder="—" />
          </div>
          <div>
            <label className="label">Valor mín. (custo)</label>
            <input type="number" className="input w-32" value={filtros.valorMin}
              onChange={(e) => setFiltro('valorMin', e.target.value)} placeholder="R$ 0" />
          </div>
          <div>
            <label className="label">Valor máx. (custo)</label>
            <input type="number" className="input w-32" value={filtros.valorMax}
              onChange={(e) => setFiltro('valorMax', e.target.value)} placeholder="—" />
          </div>
          {filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="btn-secondary text-xs flex items-center gap-1">
              <X size={12} /> Limpar filtros
            </button>
          )}
          <div className="text-xs text-ink-500 ml-auto">
            {produtosFiltrados.length} / {data.total_produtos} produto(s)
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th onClick={() => toggleSort('codigo')} className="cursor-pointer select-none">Código{arrow('codigo')}</th>
              <th onClick={() => toggleSort('descricao')} className="cursor-pointer select-none">Descrição{arrow('descricao')}</th>
              <th onClick={() => toggleSort('categoria')} className="cursor-pointer select-none">Categoria{arrow('categoria')}</th>
              <th>Marca</th>
              <th>Unid</th>
              <th onClick={() => toggleSort('atual')} className="text-right cursor-pointer select-none">Atual{arrow('atual')}</th>
              <th onClick={() => toggleSort('minimo')} className="text-right cursor-pointer select-none">Mínimo{arrow('minimo')}</th>
              <th>Status</th>
              <th onClick={() => toggleSort('custo')} className="text-right cursor-pointer select-none">Valor (Custo){arrow('custo')}</th>
              <th onClick={() => toggleSort('venda')} className="text-right cursor-pointer select-none">Valor (Venda){arrow('venda')}</th>
            </tr>
          </thead>
          <tbody>
            {produtosFiltrados.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-ink-500">Nenhum produto.</td></tr>
            )}
            {produtosFiltrados.map((p: any) => {
              const cfg = STATUS_CFG[p.status]
              const Icon = cfg.icon
              return (
                <tr key={p.id} className={p.ativo === false ? 'opacity-50' : ''}>
                  <td className="font-mono text-xs">{p.codigo}</td>
                  <td className="font-medium">{p.descricao}</td>
                  <td className="text-ink-600 text-xs">{p.categoria}</td>
                  <td className="text-ink-600 text-xs">{p.marca || '—'}</td>
                  <td className="text-ink-600">{p.unidade}</td>
                  <td className="text-right font-medium">{Number(p.estoque_atual).toLocaleString('pt-BR')}</td>
                  <td className="text-right text-ink-600">{Number(p.estoque_minimo).toLocaleString('pt-BR')}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${cfg.cls}`}>
                      <Icon size={12} />
                      {cfg.label}
                    </span>
                  </td>
                  <td className="text-right">{formatMoney(p.valor_estoque_custo)}</td>
                  <td className="text-right font-medium">{formatMoney(p.valor_estoque_venda)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Gráfico por categoria */}
      {data.barras_por_categoria.length > 0 && (
        <div className="card card-body">
          <h3 className="font-semibold text-ink-900 mb-3">Valor de Estoque por Categoria (Custo)</h3>
          <div style={{ height: Math.max(200, data.barras_por_categoria.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.barras_por_categoria} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceef2" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => 'R$' + (v / 1000).toFixed(0) + 'k'} />
                <YAxis type="category" dataKey="categoria" tick={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(v: any) => formatMoney(v)} />
                <Bar dataKey="valor" fill="#0069c4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}

function KPI({ label, value, icon: Icon, tone }: { label: string; value: string; icon: any; tone: 'brand' | 'green' | 'amber' | 'red' }) {
  const t: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  }
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-ink-500 uppercase tracking-wider">{label}</div>
          <div className="text-xl font-bold text-ink-900 mt-1">{value}</div>
        </div>
        <div className={`p-2 rounded-lg ${t[tone]}`}><Icon size={16} /></div>
      </div>
    </div>
  )
}